// ! Note these are possibly temporary, depending on what we will receive from the backend during development
export type Domain =
  | "child-care"
  | "community-work"
  | "youth-support"
  | "maternity-care"
  | "head-office";

export const domainLabels: Record<Domain, string> = {
  "maternity-care": "kraamzorg",
  "child-care": "kinderopvang",
  "youth-support": "jeugdhulp",
  "community-work": "buurtwerk",
  "head-office": "hoofdkantoor",
};

export type Image = {
  src: string;
  alt?: string | null;
  width?: number | null;
  height?: number | null;
  focalPointX?: number | null;
  focalPointY?: number | null;
};

export type Link = {
  url: string;
  title: string;
  openInNewTab?: boolean;
};

export type Vacancy = {
  id: string;
  domain: Domain;
  image: Image;
  title: string;
  cities: string[];
  rangeHours: string[];
  employmentType?: string;
  url: string;
  position?: Position;
  regions: { northeast: Position; southwest: Position }[];
  jobProfiles: string[];
  publicationStartDate: string;
};

export type Position = {
  latitude: number;
  longitude: number;
};

export type VacancyFilter = {
  title: string;
  value: string | null;
  inverted: boolean;
};

export type IllustrationType =
  | "bread"
  | "arrow"
  | "sun"
  | "walk-hands"
  | "run"
  | "building"
  | "play-football"
  | "baby-bottle"
  | "person-heart"
  | "play-blocks"
  | "tea";

export type UmbracoRichText = {
  tag: "#root";
  attributes?: Record<string, unknown>;
  elements: UmbracoRichTextElement[];
  blocks?: unknown[];
};

export type UmbracoRichTextElement =
  | {
      tag:
        | "p"
        | "div"
        | "span"
        | "em"
        | "strong"
        | "h2"
        | "h3"
        | "ul"
        | "ol"
        | "li"
        | "br";
      attributes?: Record<string, unknown>;
      elements: UmbracoRichTextElement[];
    }
  | {
      tag: "#text";
      text: string;
    }
  | {
      tag: "a";
      attributes: {
        title: string;
        href?: string;
        target?: string;
        class?: string;
        style?: string;
        anchor?: string;
        route?: {
          path: string;
          queryString?: string;
          startItem: {
            id: string;
            path: string;
          };
        };
      };
      elements: UmbracoRichTextElement[];
    };

export type Form = {
  // `title` is no longer part of the API form object; kept optional for backward compat.
  title?: string | null;
  id: string;
  type: string;
  pages: FormPage[];
  messageOnSubmit?: string | null;
  messageOnSubmitIsHtml?: boolean;
  redirectUrlOnSubmit?: string | null;
};

// A form is delivered nested: Form > pages > rows (Umbraco fieldsets) > columns > fields.
export type FormPage = {
  title?: string | null;
  condition?: FormFieldCondition | null;
  rows: FormRow[];
};

export type FormRow = {
  id?: string | null;
  title?: string | null;
  condition?: FormFieldCondition | null;
  columns: FormColumn[];
};

export type FormColumn = {
  title?: string | null;
  // Umbraco column width on a 12-column grid (1–12).
  width: number;
  fields: FormField[];
};

export type FormFieldCondition = {
  action: "Show" | "Hide";
  logic: "All" | "Any";
  rules: {
    field: string;
    operator:
      | "Is"
      | "IsNot"
      | "Contains"
      | "StartsWith"
      | "EndsWith"
      | "GreaterThen"
      | "LessThen"
      | "ContainsIgnoreCase"
      | "StartsWithIgnoreCase"
      | "EndsWithIgnoreCase"
      | "NotContains"
      | "NotContainsIgnoreCase"
      | "NotStartsWith"
      | "NotStartsWithIgnoreCase"
      | "NotEndsWith"
      | "NotEndsWithIgnoreCase";
    value: string;
  }[];
};

export type FormField = {
  id: string;
  alias: string;
  label: string;
  description: string | null;
  required: boolean;
  requiredErrorMessage: string | null;
  placeholder: string | null;
  // Server-provided initial value (e.g. hidden fields carrying context ids).
  defaultValue?: string | null;
  condition?: FormFieldCondition | null;
} & (
  | {
      type: "textBox";
      subType: "emailAddress" | "phoneNumber" | "zipCode" | null;
    }
  | {
      // Not rendered; submitted with its `defaultValue` (e.g. endpointId/activityId).
      // Also covers the CMS-configured DataLayer field: its `defaultValue` is a JSON
      // string ({ FormName, Data, Fields }), resolved in Form.vue's resolveDataLayerData.
      type: "hidden";
    }
  | {
      type: "textarea";
    }
  | {
      type: "dropdown";
      options: {
        value: string;
        label: string;
      }[];
    }
  | {
      // Enkelvoudige keuze; rendert als (verticale) RadioGroup. De CMS levert dit type
      // (nog) aan als "radioGroup"; beide literals worden geaccepteerd en op dezelfde
      // RadioGroup gerenderd zolang er geen backend-mapper is.
      type: "radio" | "radioGroup";
      options: {
        value: string;
        label: string;
      }[];
    }
  | {
      // Dropdown met capaciteit: de resterende plekken zitten (voorlopig) in het
      // optie-label na een pipe, bv. "Maandag 10:00|3". Zie plan/backend-gat.
      type: "dropdownWithCap";
      options: {
        value: string;
        label: string;
      }[];
    }
  | {
      // Datumkiezer met vast venster: kiesbare datum tussen minDate en maxDate.
      // Instellingen komen van de backend als een generieke settings-dictionary.
      type: "staticDatePicker";
      settings?: {
        minDate?: string | null;
        maxDate?: string | null;
        disabledWeekdays?: number[];
      };
    }
  | {
      // Datumkiezer met dynamisch venster: vandaag − minDays t/m vandaag + maxDays.
      type: "dynamicDatePicker";
      settings?: {
        minDays?: string | null;
        maxDays?: string | null;
        disabledWeekdays?: number[];
      };
    }
  | {
      // Enkele datumkiezer zonder venster (Umbraco "Date"-veld).
      type: "datePicker";
    }
  | {
      type: "fileUpload";
    }
  | {
      type: "multipleFileUpload";
    }
  | {
      type: "checkbox";
    }
  | {
      // Umbraco's "Data Consent": verplichte AVG-toestemming. De backend zet de
      // `acceptCopy`-instelling in `description`; dat is de HTML met o.a. de privacylink
      // en dus de tekst naast het vinkje. `label` is hier vaak leeg (showLabel = false).
      type: "dataconsent";
    }
  | {
      type: "checkBoxList";
      options: {
        value: string;
        label: string;
      }[];
    }
  | {
      // KTO-veld "Single choice feedback": één keuze uit (doorgaans vijf) opties, elk met
      // een Gruppet-emoticon. De backend levert alleen labels en waarden; de emoticon
      // wordt op volgorde gekozen, net als in de oude Razor-view.
      type: "singleChoiceFeedback";
      options: {
        value: string;
        label: string;
      }[];
    }
  | {
      // KTO-veld "Single choice range": één keuze uit een geordende lijst opties, getoond
      // als schuifbalk. De volgorde van `options` bepaalt de positie op de schaal; de
      // backend stuurt geen min/max/step mee.
      type: "singleChoiceRange";
      options: {
        value: string;
        label: string;
      }[];
    }
  | {
      // KTO-veld "gro-up location picker": drie afhankelijke dropdowns (plaats →
      // opvangsoort → locatie) die samen één waarde opleveren, in het formaat
      // "{locatienaam} ({locatieId})". De opties komen van de TownApi, niet uit het
      // formuliercontract; `options` is hier dus altijd leeg.
      type: "groUpLocation";
    }
  | {
      type: "titleAndDescription";
      subType: "h2" | "h3" | "h4" | "h5" | "h6" | null | "";
    }
  | {
      // Rich text block: display-only, no input. The HTML lives in `description`.
      type: "richText";
    }
  | {
      type: "reCaptcha";
    }
);

export type SharedUspBlock = {
  title: string;
  description: UmbracoRichText;
  image: Image;
};

export type ComponentUspBlock = {
  title?: string | null;
  description?: UmbracoRichText | null;
  items: SharedUspBlock[];
  background?: string | null;
};
